import toast from 'react-hot-toast'
import { Toast } from '../types'
import { settingsService } from './settings.service'

export type ToastPopupQueueLane = 'default' | 'tail'

export interface ShowToastOptions {
  popupLane?: ToastPopupQueueLane
}

class ToastService {
  private toasts: Toast[] = []
  private subscribers: ((toasts: Toast[]) => void)[] = []
  private _suppressPopupToasts: boolean = false
  private showToastCallbacks: ((toast: Toast | null) => void)[] = []

  private _popupSlotOccupied = false
  private _currentPopupToastId: string | null = null
  private _popupMainQueue: Toast[] = []
  private _popupTailQueue: Toast[] = []
  private _pendingTailToast: Toast | null = null
  private _pendingTailTimer: ReturnType<typeof setTimeout> | null = null
  private static readonly TAIL_DEFER_MS = 1500

  get suppressPopupToasts(): boolean {
    return this._suppressPopupToasts
  }

  set suppressPopupToasts(value: boolean) {
    const wasSuppressed = this._suppressPopupToasts
    this._suppressPopupToasts = value
    if (value) {
      this._popupSlotOccupied = false
      this._currentPopupToastId = null
      this.cancelPendingTailTimer()
      this.notifyShowToastCallbacks(null)
    } else if (wasSuppressed && !value) {
      this.tryShowNextPopup()
    }
  }

  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  private shouldDisplayForCurrentOrganization(toastObj: Toast): boolean {
    const toastOrgId = toastObj.organizationId
    if (toastOrgId == null) {
      return true
    }
    const currentOrgId = settingsService.getCurrentOrganizationId()
    if (currentOrgId == null) {
      return true
    }
    return toastOrgId === currentOrgId
  }

  show(toastObj: Toast, options?: ShowToastOptions): void {
    if (!toastObj.id) {
      toastObj.id = this.generateUUID()
    }

    if (!this.shouldDisplayForCurrentOrganization(toastObj)) {
      return
    }

    // Parse error if it's a string
    if (typeof toastObj.error === 'string') {
      try {
        toastObj.error = JSON.parse(toastObj.error)
      } catch (e) {
        // Keep as string if parsing fails
      }
    }

    // Add to internal list (like React - unshift to add at beginning)
    this.toasts.unshift(toastObj)

    if (!this._suppressPopupToasts) {
      this.enqueueOrShowPopup(toastObj, options?.popupLane ?? 'default')
    }

    this.notifySubscribers()
  }

  showError(content: string, error: any = null, delay: number = 10000): void {
    this.show({
      content: content,
      delay: delay,
      classname: 'error',
      error,
      organizationId: settingsService.getCurrentOrganizationId() ?? null,
    })
  }

  showInfo(content: string, delay: number = 5000): void {
    this.show({
      content: content,
      delay: delay,
      classname: 'info',
      organizationId: settingsService.getCurrentOrganizationId() ?? null,
    })
  }

  showSuccess(content: string, delay: number = 5000): void {
    this.show({
      content: content,
      delay: delay,
      classname: 'success',
      organizationId: settingsService.getCurrentOrganizationId() ?? null,
    })
  }

  closeToast(toastObj: Toast): void {
    const index = this.toasts.findIndex((t) => t.id === toastObj.id)
    if (index > -1) {
      this.toasts.splice(index, 1)
      this.notifySubscribers()
    }
    this.removeFromPopupQueues(toastObj.id)
  }

  getToasts(): Toast[] {
    return this.toasts
  }

  clearToasts(): void {
    this.toasts = []
    this._popupMainQueue = []
    this._popupTailQueue = []
    this._popupSlotOccupied = false
    this._currentPopupToastId = null
    this.cancelPendingTailTimer()
    this.notifySubscribers()
    this.notifyShowToastCallbacks(null)
  }

  onPopupToastDismissed(closedId: string | undefined): void {
    if (!closedId || this._currentPopupToastId !== closedId) {
      return
    }
    this._currentPopupToastId = null
    this._popupSlotOccupied = false
    this.tryShowNextPopup()
  }

  subscribeToToasts(callback: (toasts: Toast[]) => void): () => void {
    this.subscribers.push(callback)
    callback(this.toasts)

    return () => {
      const index = this.subscribers.indexOf(callback)
      if (index > -1) {
        this.subscribers.splice(index, 1)
      }
    }
  }

  // Subscribe to individual toast shows (like React's showToast Subject)
  subscribeToShowToast(callback: (toast: Toast | null) => void): () => void {
    this.showToastCallbacks.push(callback)

    return () => {
      const index = this.showToastCallbacks.indexOf(callback)
      if (index > -1) {
        this.showToastCallbacks.splice(index, 1)
      }
    }
  }

  // Helper methods for direct popup toasts (using react-hot-toast)
  success(message: string, options?: any): string {
    return toast.success(message, {
      duration: 4000,
      position: 'bottom-right',
      ...options,
    })
  }

  error(message: string, options?: any): string {
    return toast.error(message, {
      duration: 6000,
      position: 'bottom-right',
      ...options,
    })
  }

  loading(message: string, options?: any): string {
    return toast.loading(message, {
      position: 'bottom-right',
      ...options,
    })
  }

  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string
      error: string
    },
    options?: any
  ): Promise<T> {
    return toast.promise(promise, messages, {
      position: 'bottom-right',
      ...options,
    })
  }

  dismiss(toastId?: string): void {
    if (toastId) {
      toast.dismiss(toastId)
    } else {
      toast.dismiss()
    }
  }

  remove(toastId: string): void {
    toast.remove(toastId)
  }

  custom(content: React.ReactNode, options?: any): string {
    return toast.custom(content as any, {
      position: 'bottom-right',
      ...options,
    })
  }

  private removeFromPopupQueues(toastId: string | undefined): void {
    if (!toastId) return
    this._popupMainQueue = this._popupMainQueue.filter((t) => t.id !== toastId)
    this._popupTailQueue = this._popupTailQueue.filter((t) => t.id !== toastId)
    if (this._pendingTailToast?.id === toastId) {
      this.cancelPendingTailTimer()
    }
  }

  private cancelPendingTailTimer(): void {
    if (this._pendingTailTimer != null) {
      clearTimeout(this._pendingTailTimer)
      this._pendingTailTimer = null
    }
    this._pendingTailToast = null
  }

  private enqueueOrShowPopup(toastObj: Toast, lane: ToastPopupQueueLane): void {
    if (this._suppressPopupToasts) {
      return
    }

    if (lane === 'tail') {
      if (this._popupSlotOccupied || this._pendingTailToast) {
        this._popupTailQueue.push(toastObj)
        return
      }
      this._pendingTailToast = toastObj
      this._pendingTailTimer = setTimeout(() => {
        this._pendingTailTimer = null
        const pending = this._pendingTailToast
        this._pendingTailToast = null
        if (!pending || this._suppressPopupToasts) return
        if (this._popupSlotOccupied) {
          this._popupTailQueue.push(pending)
          return
        }
        this._popupSlotOccupied = true
        this._currentPopupToastId = pending.id ?? null
        this.notifyShowToastCallbacks(pending)
      }, ToastService.TAIL_DEFER_MS)
      return
    }

    if (this._pendingTailToast) {
      const deferred = this._pendingTailToast
      this.cancelPendingTailTimer()
      this._popupTailQueue.push(deferred)
    }

    if (this._popupSlotOccupied) {
      this._popupMainQueue.push(toastObj)
      return
    }
    this._popupSlotOccupied = true
    this._currentPopupToastId = toastObj.id ?? null
    this.notifyShowToastCallbacks(toastObj)
  }

  private tryShowNextPopup(): void {
    if (this._suppressPopupToasts || this._popupSlotOccupied) {
      return
    }
    const next = this._popupMainQueue.shift() ?? this._popupTailQueue.shift()
    if (next) {
      this._popupSlotOccupied = true
      this._currentPopupToastId = next.id ?? null
      this.notifyShowToastCallbacks(next)
    } else {
      this._currentPopupToastId = null
      this.notifyShowToastCallbacks(null)
    }
  }

  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback(this.toasts))
  }

  private notifyShowToastCallbacks(toastObj: Toast | null): void {
    this.showToastCallbacks.forEach((callback) => callback(toastObj))
  }
}

export const toastService = new ToastService()
